import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';
import { useAppContext } from '../AppContext';
import { StyleSandbox } from '../components/StyleSandbox';
import { TryOnMirror } from '../components/TryOnMirror';
import Canvas from '../components/Canvas';
import { useWardrobe } from '../hooks/useWardrobe';
import { LayoutGridIcon, UserIcon, CameraIcon, SparklesIcon } from '../components/icons';
import { UserProfile, OutfitLayer, AppPage } from '../types';
import { hasAssistantAccess } from '@shared/services/userService';
import { POSE_CATEGORIES } from '../constants';

type StyleLabMode = 'moodboard' | 'fitting' | 'live';

interface StyleLabPageProps {
    userProfile: UserProfile;
    currentUserId: string;
    currentLayer: OutfitLayer;
    baseModelImageUrl: string | null;
    isLoading: boolean;
    loadingMessage: string;
    loadingAction: string | null;
    error: string | null;
    onClearError: () => void;
    handlePoseSelect: (pose: string) => void;
    POSE_CATEGORIES: any[];
    handleSceneUpdate: (config: any) => void;
    handleGenerate3DViews: () => Promise<boolean>;
    handleToggleFavoritePose: (pose: string) => void;
    onSaveOutfit: () => void;
    onStartOver: () => void;
    onLoadOutfit?: (outfit: any) => void;
    onCreativeEdit?: (type: 'ageing' | 'wrinkles' | 'golden_hour' | 'remove_glasses' | 'remove_hat' | 'remove_watch' | 'remove_bag') => Promise<void>;
    onSaveReport?: (report: any, imageUrl: string) => void;
    styleReport: any;
    onCloseReport: () => void;
    onGenerateReport: () => void;
    onNavigate: (page: AppPage, params?: any) => void;
    onItemAdd?: any; // New prop from App.tsx
}

const StyleLabPage: React.FC<StyleLabPageProps> = (props) => {
    const {
        userProfile, currentUserId, currentLayer, baseModelImageUrl,
        isLoading, loadingMessage, loadingAction, error, onClearError,
        handlePoseSelect, POSE_CATEGORIES, handleSceneUpdate, handleGenerate3DViews,
        handleToggleFavoritePose, onSaveOutfit, onStartOver, onLoadOutfit,
        onCreativeEdit, onSaveReport, styleReport, onCloseReport, onGenerateReport, onNavigate,
        onItemAdd
    } = props;
    const { t } = useTranslation();
    const { wardrobe } = useAppContext();
    const [activeMode, setActiveMode] = useState<StyleLabMode>('fitting');
    const [searchParams, setSearchParams] = useSearchParams();

    // Sync mode with URL params if present
    useEffect(() => {
        const mode = searchParams.get('mode') as StyleLabMode;
        if (mode && ['moodboard', 'fitting', 'live'].includes(mode)) {
            setActiveMode(mode);
        }
    }, [searchParams]);

    const handleModeChange = (mode: StyleLabMode) => {
        setActiveMode(mode);
        setSearchParams({ mode }, { replace: true });
    };

    // Convert current outfit to sandbox items
    const syncOutfitToSandbox = (): any[] => {
        const items: any[] = [];
        let zIndex = 1;

        // Process garments
        if (currentLayer.garments) {
            currentLayer.garments.forEach((g: any) => {
                items.push({
                    ...g,
                    instanceId: `${g.id}-sync-${Date.now()}`,
                    x: 40 + Math.random() * 20,
                    y: 30 + Math.random() * 40,
                    rotation: 0,
                    scale: 1,
                    zIndex: zIndex++
                });
            });
        }

        // Process accessories
        if (currentLayer.accessories) {
            currentLayer.accessories.forEach((a: any) => {
                items.push({
                    ...a,
                    instanceId: `${a.id}-sync-${Date.now()}`,
                    x: 60 + Math.random() * 20,
                    y: 20 + Math.random() * 20,
                    rotation: 0,
                    scale: 0.8,
                    zIndex: zIndex++
                });
            });
        }

        return items;
    };

    const handleApplySandboxToModel = (sandboxItems: any[]) => {
        if (!onLoadOutfit) return;

        const garments = sandboxItems.filter(i => i.type === 'garment');
        const accessories = sandboxItems.filter(i => i.type === 'accessory');

        const newLayer: OutfitLayer = {
            ...currentLayer,
            garments: garments,
            accessories: accessories,
            mainGarment: garments[0], // UIX compatibility
            appliedGarmentColors: garments.reduce((acc, g) => ({ ...acc, [g.id]: g.defaultColor || '#000000' }), {}),
            appliedAccessoryColors: accessories.reduce((acc, a) => ({ ...acc, [a.id]: a.defaultColor || '#000000' }), {}),
            // Reset outputs to force regeneration with new items
            poseOutputs: {},
            currentPoseInstruction: 'front_view'
        };

        onLoadOutfit({
            id: 'sync-' + Date.now(),
            name: 'Moodboard Design',
            layer: newLayer,
            baseModelImageUrl: baseModelImageUrl
        });

        setActiveMode('fitting');
        setSearchParams({ mode: 'fitting' }, { replace: true });
    };

    const handleShopTheLook = () => {
        if (!hasAssistantAccess(userProfile)) {
            onNavigate('services');
            return;
        }

        const itemNames = [
            ...(currentLayer.garments || []).map(g => g.name || g.id),
            ...(currentLayer.accessories || []).map(a => a.name || a.id)
        ];

        if (itemNames.length === 0) {
            onNavigate('styling-advisor', { section: 'shopping_assistant' });
            return;
        }

        const query = `Shop the look: ${itemNames.join(', ')}`;
        onNavigate('styling-advisor', { section: 'shopping_assistant', query });
    };

    const modes = [
        { id: 'moodboard', label: t('nav_studio'), icon: LayoutGridIcon },
        { id: 'fitting', label: t('nav_dressingRoom'), icon: UserIcon },
        { id: 'live', label: t('nav_tryon'), icon: CameraIcon },
    ];

    return (
        <div className="h-screen w-full flex flex-col bg-white dark:bg-gray-950 overflow-hidden">
            {/* Mobile-First Floating Mode Switcher */}
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 w-full max-w-md">
                <div className="glass dark:bg-gray-900/60 rounded-full p-1.5 shadow-2xl flex items-center justify-between border border-white/20 dark:border-white/5 backdrop-blur-xl">
                    {modes.map((mode) => (
                        <button
                            key={mode.id}
                            onClick={() => handleModeChange(mode.id as StyleLabMode)}
                            className={`flex-1 flex flex-col items-center gap-1 py-2 px-1 rounded-full transition-all duration-300 ${activeMode === mode.id
                                ? 'bg-indigo-600 text-white shadow-lg scale-105'
                                : 'text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
                                }`}
                        >
                            <mode.icon className={`w-5 h-5 ${activeMode === mode.id ? 'animate-pulse' : ''}`} />
                            <span className="text-[10px] font-bold uppercase tracking-wider">{mode.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-grow relative overflow-hidden">
                <AnimatePresence mode="wait">
                    {activeMode === 'moodboard' && (
                        <motion.div
                            key="moodboard"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="h-full w-full"
                        >
                            <StyleSandbox
                                wardrobe={wardrobe}
                                userProfile={userProfile}
                                onSaveOutfit={(items) => {
                                    console.log("Saving Moodboard:", items);
                                    alert(t('common_saved_success'));
                                }}
                                currentUserId={currentUserId}
                                onApplyToModel={handleApplySandboxToModel}
                                baseModelImageUrl={baseModelImageUrl}
                                initialItems={syncOutfitToSandbox()}
                                injectItem={onItemAdd}
                            />
                        </motion.div>
                    )}

                    {activeMode === 'fitting' && (
                        <motion.div
                            key="fitting"
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            className="h-full w-full"
                        >
                            <Canvas
                                displayImageUrl={currentLayer.poseOutputs[currentLayer.currentPoseInstruction] || baseModelImageUrl || null}
                                baseModelImageUrl={baseModelImageUrl}
                                isLoading={isLoading}
                                loadingMessage={loadingMessage}
                                loadingAction={loadingAction}
                                generationError={error}
                                onClearError={onClearError}
                                onSelectPose={handlePoseSelect}
                                poseCategories={POSE_CATEGORIES}
                                currentPoseInstruction={currentLayer.currentPoseInstruction}
                                availablePoseKeys={Object.keys(currentLayer.poseOutputs)}
                                onShopTheLook={handleShopTheLook}
                                isShopTheLookEnabled={true}
                                isFavorited={false} // Would need favoritedOutfitIds check here if we want it
                                onToggleFavorite={() => { }}
                                isFavoriteEnabled={true}
                                userProfile={userProfile}
                                isComparisonMode={false}
                                onToggleComparisonMode={() => { }}
                                onSaveOutfit={onSaveOutfit}
                                onStartOver={onStartOver}
                                comparisonOutfits={null}
                                currentOutfit={currentLayer}
                                onUpdateSceneConfig={handleSceneUpdate}
                                onGenerate3DView={handleGenerate3DViews}
                                view360Images={[]} // Populated by 3D logic
                                onToggleFavoritePose={handleToggleFavoritePose}
                                onNavigate={onNavigate}
                                onApplyCreativeTool={onCreativeEdit}
                                onSaveReport={onSaveReport}
                                styleReport={styleReport}
                                onCloseReport={onCloseReport}
                                onGenerateReport={onGenerateReport}
                            />
                        </motion.div>
                    )}

                    {activeMode === 'live' && (
                        <motion.div
                            key="live"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className="h-full w-full p-6 md:p-12 bg-gray-900"
                        >
                            <div className="max-w-4xl mx-auto h-full max-h-[80vh]">
                                <TryOnMirror />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default StyleLabPage;
